import { FC, useState } from 'react';
import { IRecording } from '../../appointments/recording/RecordingState';
import { IonAccordion, IonAccordionGroup, IonBadge, IonCheckbox, IonIcon, IonItem, IonLabel, IonList } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { RecordingState } from '../../appointments/recording/RecordingState';
import { calendar, today, refresh, timeOutline, personOutline, documentTextOutline } from 'ionicons/icons';

interface CategorizedActionItemsProps {
    recording: IRecording;
    noteId: string;
    readonly?: boolean;
}

export const CategorizedActionItems: FC<CategorizedActionItemsProps> = ({ recording, noteId, readonly = false }) => {
    const recordingState = useInstance(RecordingState);
    const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
    const [openSections, setOpenSections] = useState<string[]>(['take-action-today']); // Default to opening urgent section
    const [saving, setSaving] = useState(false);

    const categorizedItems = recordingState.getActionItemsByCategory(recording);

    // Define toggle function before any early returns
    const handleToggleComplete = async (actionItemId: string, event?: any) => {
        if (readonly || saving) {
            return;
        }

        setSaving(true);

        // Prevent the click from bubbling up to the accordion
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        setLoadingItems(prev => new Set(prev).add(actionItemId));

        try {
            await recordingState.toggleActionItemCompletion(noteId, actionItemId);
        } finally {
            setLoadingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(actionItemId);
                return newSet;
            });

            setSaving(false);
        }
    };

    const hasCategorizedItems = Boolean(recording?.summary?.actionItems?.some(item => item.category));
    const categoriesToShow = hasCategorizedItems ? Object.entries(categorizedItems).filter(([_cat, items]) => Boolean(items.length)) : [];

    if (categoriesToShow.length === 0) {
        // Show simple list if no categorization was applied
        const allItems = Object.values(categorizedItems).flat();
        if (allItems.length === 0) {
            return null;
        }

        return (
            <div
                data-id="action-items-simple-list"
                style={{display: 'flex', flexDirection: 'column', gap: 12, padding: '16px'}}
            >
                {allItems.map((item: any, i) => (
                    <div
                        key={`item-${i}`}
                        data-id={`action-item-${item.id}`}
                        data-category="uncategorized"
                        style={{display: 'flex', alignItems: 'flex-start', gap: 12}}
                    >
                        <IonCheckbox
                            data-id={`action-item-checkbox-${item.id}`}
                            checked={item.completed || false}
                            disabled={readonly || saving || loadingItems.has(item.id)}
                            onIonChange={(e) => !readonly && handleToggleComplete(item.id, e)}
                            style={{
                                marginTop: '2px',
                                '--size': '20px',
                                '--checkbox-background-checked': 'var(--ion-color-success)',
                                '--border-color-checked': 'var(--ion-color-success)'
                            }}
                        />
                        <div style={{flex: 1, opacity: loadingItems.has(item.id) ? 0.5 : 1}}>
                            <div style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                textDecoration: item.completed ? 'line-through' : 'none',
                                opacity: item.completed ? 0.7 : 1,
                                marginBottom: item.details || item.when || item.assignedTo ? '8px' : '0'
                            }}>
                                {item.description}
                            </div>

                            {/* Show metadata if available */}
                            {item.when && (
                                <div style={{fontSize: '14px', color: 'var(--ion-color-medium)', marginBottom: '4px'}}>
                                    <strong>When:</strong> {item.when}
                                </div>
                            )}
                            {item.assignedTo && (
                                <div style={{fontSize: '14px', color: 'var(--ion-color-medium)', marginBottom: '4px'}}>
                                    <strong>Assigned to:</strong> {item.assignedTo}
                                </div>
                            )}
                            {item.details && (
                                <div style={{fontSize: '14px', color: 'var(--ion-color-medium)', lineHeight: '1.4', marginBottom: '4px'}}>
                                    <strong>Details:</strong> {item.details}
                                </div>
                            )}

                            {/* Completion status */}
                            {item.completedAt && (
                                <div style={{
                                    fontSize: '12px',
                                    color: 'var(--ion-color-medium)',
                                    marginTop: '8px',
                                    fontStyle: 'italic'
                                }}>
                                    Completed {new Date(item.completedAt).toLocaleDateString()}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    const getCategoryIcon = (category: string) => {
        const iconMap: Record<string, string> = {
            'take-action-today': today,
            'schedule-these': calendar,
            'ongoing-care': refresh,
            'uncategorized': ''
        };
        return iconMap[category];
    };

    const getCategoryColor = (category: string) => {
        const colorMap: Record<string, string> = {
            'take-action-today': 'secondary',
            'schedule-these': 'tertiary',
            'ongoing-care': 'success',
            'uncategorized': 'medium'
        };
        return colorMap[category] || 'medium';
    };

    const getCategoryBackgroundStyle = (category: string) => {
        const styleMap: Record<string, React.CSSProperties> = {
            'take-action-today': {
                backgroundColor: 'rgba(var(--ion-color-secondary-rgb), 0.12)', // Secondary with 12% opacity
                border: '1px solid rgba(var(--ion-color-secondary-rgb), 0.3)',
                borderRadius: '8px'
            },
            'schedule-these': {
                backgroundColor: 'rgba(var(--ion-color-tertiary-rgb), 0.12)', // Tertiary with 12% opacity
                border: '1px solid rgba(var(--ion-color-tertiary-rgb), 0.3)',
                borderRadius: '8px'
            },
            'ongoing-care': {
                backgroundColor: 'rgba(var(--ion-color-success-rgb), 0.12)', // Success with 12% opacity
                border: '1px solid rgba(var(--ion-color-success-rgb), 0.3)',
                borderRadius: '8px'
            },
            'uncategorized': {
                backgroundColor: 'rgba(var(--ion-color-medium-rgb), 0.12)',
                border: '1px solid rgba(var(--ion-color-medium-rgb), 0.3)',
                borderRadius: '8px'
            }
        };
        return styleMap[category] || styleMap['uncategorized'];
    };


    if (readonly) {
        // Simple display for readonly mode - just show all items without categories
        return (
            <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                {Object.values(categorizedItems).flat().map((item: any, i) => (
                    <div key={`item-${i}`} style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <IonCheckbox checked={item.completed || false} disabled />
                        <span style={{
                            textDecoration: item.completed ? 'line-through' : 'none',
                            color: item.completed ? 'var(--ion-color-medium)' : 'var(--ion-color-dark)'
                        }}>
                            {item.description}
                        </span>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <IonAccordionGroup
            data-id="action-items-categorized"
            multiple
            value={openSections}
            onIonChange={(e) => setOpenSections(e.detail.value)}
        >
            {categoriesToShow.map(([category, items]) => {
                const stats = recordingState.getCategoryCompletionStats(recording, category);
                const displayName = recordingState.getCategoryDisplayName(category);
                const categoryIcon = getCategoryIcon(category);
                const categoryColor = getCategoryColor(category);

                return (
                    <IonAccordion
                        key={category}
                        value={category}
                        data-id={`action-items-category-${category}`}
                    >
                        <IonItem
                            slot="header"
                            data-id={`action-items-category-header-${category}`}
                            style={{
                                ...getCategoryBackgroundStyle(category),
                                margin: '8px 16px',
                                '--background': 'transparent'
                            }}
                        >
                            <div style={{display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between'}}>
                                <div style={{display: 'flex', alignItems: 'center'}}>
                                    {categoryIcon && (
                                        <IonIcon
                                            icon={categoryIcon}
                                            color={categoryColor}
                                            style={{marginRight: '12px', fontSize: '20px'}}
                                        />
                                    )}
                                    <IonLabel>
                                        <h3 style={{margin: 0, color: 'var(--ion-color-dark)', fontSize: '16px', fontWeight: '600'}}>
                                            {displayName}
                                        </h3>
                                    </IonLabel>
                                </div>
                                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                                    <IonBadge
                                        style={{
                                            backgroundColor: `rgba(var(--ion-color-${categoryColor}-rgb), 0.5)`,
                                            color: 'var(--ion-color-dark)',
                                            '--color': 'var(--ion-color-dark)',
                                            fontWeight: '600'
                                        }}
                                    >
                                        {stats.completed}/{stats.total}
                                    </IonBadge>
                                </div>
                            </div>
                        </IonItem>

                        <div slot="content">
                            <IonList data-id={`action-items-list-${category}`}>
                                {items.map((item: any) => (
                                    <div
                                        key={item.id}
                                        data-id={`action-item-${item.id}`}
                                        data-category={category}
                                        style={{ marginBottom: '16px' }}
                                    >
                                        <IonItem
                                            data-id={`action-item-container-${item.id}`}
                                            style={{
                                                '--padding-start': '16px',
                                                '--inner-padding-end': '16px',
                                                opacity: item.completed ? 0.6 : 1,
                                                alignItems: 'flex-start',
                                                position: 'relative'
                                            }}
                                            disabled={saving}
                                            onClick={(e) => e.stopPropagation()} // Prevent accordion from toggling
                                        >
                                            <IonCheckbox
                                                slot="start"
                                                data-id={`action-item-checkbox-${item.id}`}
                                                checked={item.completed || false}
                                                onIonChange={(e) => handleToggleComplete(item.id, e)}
                                                onClick={(e) => e.stopPropagation()} // Additional protection
                                                style={{
                                                    marginRight: '12px',
                                                    marginTop: '10px',
                                                    '--size': '20px',
                                                    '--checkbox-background-checked': 'var(--ion-color-success)',
                                                    '--border-color-checked': 'var(--ion-color-success)'
                                                }}
                                            />
                                            <IonLabel style={{opacity: loadingItems.has(item.id) ? 0.5 : 1}}>
                                                {/* Title */}
                                                <div style={{
                                                    fontSize: '16px',
                                                    fontWeight: '600',
                                                    textDecoration: item.completed ? 'line-through' : 'none',
                                                    opacity: item.completed ? 0.7 : 1,
                                                    marginBottom: '8px'
                                                }}>
                                                    {item.description}
                                                </div>

                                                {/* When */}
                                                {item.when && (
                                                    <div style={{
                                                        fontSize: '14px',
                                                        color: 'var(--ion-color-dark)',
                                                        marginBottom: '4px',
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '6px'
                                                    }}>
                                                        <IonIcon icon={timeOutline} style={{ fontSize: '16px', color: 'var(--ion-color-medium)', marginTop: '1px', flexShrink: 0 }} />
                                                        <strong>When:</strong> {item.when}
                                                    </div>
                                                )}

                                                {/* Assigned to */}
                                                {item.assignedTo && (
                                                    <div style={{
                                                        fontSize: '14px',
                                                        color: 'var(--ion-color-dark)',
                                                        marginBottom: '8px',
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '6px'
                                                    }}>
                                                        <IonIcon icon={personOutline} style={{ fontSize: '16px', color: 'var(--ion-color-medium)', marginTop: '1px', flexShrink: 0 }} />
                                                        <strong>Assigned to:</strong> {item.assignedTo}
                                                    </div>
                                                )}

                                                {/* Details */}
                                                {item.details && (
                                                    <div style={{
                                                        fontSize: '14px',
                                                        color: 'var(--ion-color-dark)',
                                                        marginBottom: '12px',
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '6px',
                                                        lineHeight: '1.4'
                                                    }}>
                                                        <IonIcon icon={documentTextOutline} style={{ fontSize: '16px', color: 'var(--ion-color-medium)', marginTop: '1px', flexShrink: 0 }} />
                                                        <div>
                                                            <strong>Details:</strong> {item.details}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Completion status */}
                                                {item.completedAt && (
                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: 'var(--ion-color-medium)',
                                                        marginTop: '8px',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Completed {new Date(item.completedAt).toLocaleDateString()}
                                                    </div>
                                                )}
                                            </IonLabel>

                                        </IonItem>
                                    </div>
                                ))}
                            </IonList>
                        </div>
                    </IonAccordion>
                );
            })}
        </IonAccordionGroup>
    );
};
